/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */

import React, { useCallback, useEffect, useState } from "react";
import { IEvent } from "../../../store/api/devices/types";
import { useAlarmDisplay } from "./hooks";
import CustomDataGrid from "../../../components/Shared/Table/CustomDataGrid";
import { GridEventListener, GridRowParams, GridSortModel } from "@mui/x-data-grid";
import EventsFilter from "../../../components/Nodes/EventsFilter/EventsFilter";
import { getSortBy } from "../../../utils/helpers";
import EventDrawer from "../../../components/Drawers/EventDrawer";
import AlarmsFilter from "../../../components/Nodes/AlarmsFilter/AlarmsFilter";
import { useAlarmsFilter } from "../../../components/Nodes/AlarmsFilter/hooks";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { useStoreDispatch } from "../../../store/hooks";
import { resetEventFilters } from "../../../store/filters/events/EventFilterReducer";
import { resetEventFilters as resetAlarmFilters, setSelectedEventSeverity } from "../../../store/filters/alarms/reducers";
import { resetKeywordForScope } from "../../../store/search/reducer";
import { searchKeywordByScopeSelector } from "../../../store/search/selector";
import { setRegionFilterIds } from "../../../store/filters/Nodes/reducer";
import { setPaginationModel } from "../../../store/helpers/helperReducers";
import { initialState } from "../../../store/helpers/helperInitialState";
import { Button } from "../../../components/ui/button";
import { Icon } from "../../../components/Shared/Icon";
import { Loader2 } from "lucide-react";
import ActiveFilterChips from "../../../components/Shared/Filter/ActiveFilterChips";

interface AlarmDisplayProps {
  selectedGeoRegions?: string[];
}

interface AlarmOverviewFilterEventDetail {
  severity?: string;
}

const AlarmDisplay: React.FC<AlarmDisplayProps> = ({ selectedGeoRegions }) => {
  const location = useLocation();
  const dispatch = useStoreDispatch();
  const { data, isLoading, alarmColumns, exportHandler, exportStatus, setAlarmTableSort } = useAlarmDisplay(selectedGeoRegions);
  const { eventFilters: alarmFilters } = useAlarmsFilter();
  const alarmSearchKeyword = useSelector(searchKeywordByScopeSelector("alarms"));
  const rowsData = data?.data || [];
  const noOfRows = data?.totalItems || 0;

  const handleClearAlarmSearch = useCallback(() => {
    dispatch(resetKeywordForScope({ scope: "alarms" }));
  }, [dispatch]);

  const handleClearAllAlarmFilters = useCallback(() => {
    dispatch(resetAlarmFilters());
    dispatch(setRegionFilterIds([]));
    dispatch(resetKeywordForScope({ scope: "alarms" }));
  }, [dispatch]);

  const [rowClicked, setRowClicked] = useState(false);
  const [selectedRowData, setSelectedRowData] = useState<IEvent>();

  const handleCloseButtonClicked = (value: boolean) => {
    setRowClicked(value);
  };

  const handleRowClicked: GridEventListener<"rowClick"> = (params: GridRowParams<IEvent>) => {
    setRowClicked(true);
    setSelectedRowData(params.row);
  };

  // THIS USEEFFECT RESETS GLOBAL REDUX FILTERS TO INITIAL STATE WHEN NAVIGATED TO NODES/ALARMS FROM ALARMDATAGRID AND VICE-VERSA

  useEffect(() => {
    if (location.pathname.startsWith("/nodes/alarms")) {
      dispatch(resetEventFilters());
      dispatch(resetAlarmFilters());
      dispatch(resetKeywordForScope({ scope: "alarms" }));
      dispatch(setPaginationModel({ alarmDisplay: initialState.pagination }));
    }
  }, [dispatch, location.pathname]);

  useEffect(() => {
    const handleOverviewSeverityFilter = (event: Event) => {
      const customEvent = event as CustomEvent<AlarmOverviewFilterEventDetail>;
      const severity = customEvent.detail?.severity;

      if (!severity) return;

      // Ensure dashboard selection applies as a clean preset, not combined with stale alarm filters.
      dispatch(resetAlarmFilters());
      dispatch(setSelectedEventSeverity(severity));
      dispatch(setPaginationModel({ alarmDisplay: initialState.pagination }));
      dispatch(resetKeywordForScope({ scope: "alarms" }));
    };

    window.addEventListener("alarm-overview:filter", handleOverviewSeverityFilter as EventListener);

    return () => {
      window.removeEventListener("alarm-overview:filter", handleOverviewSeverityFilter as EventListener);
    };
  }, [dispatch]);

  return (
    <div>
      {rowClicked && <EventDrawer data={selectedRowData} setRowClicked={setRowClicked} closedButtonClicked={handleCloseButtonClicked} isAlarmContext={true} />}
      <div>
        <AlarmsFilter />
      </div>
      <div className="flex justify-between items-end mb-1 mt-2">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm text-muted-foreground">{`Showing ${noOfRows} results`}</span>
          <ActiveFilterChips
            filters={alarmFilters}
            searchKeyword={alarmSearchKeyword}
            onClearSearch={handleClearAlarmSearch}
            onClearAll={handleClearAllAlarmFilters}
            dispatch={dispatch}
          />
        </div>
        <Button
          variant="outline"
          size="icon"
          className="h-9 w-9 shrink-0 relative -top-1"
          disabled={exportStatus.isLoading}
          onClick={exportHandler}
          aria-label="Export CSV"
        >
          {exportStatus.isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Icon name="download" size={18} />}
        </Button>
      </div>
      <div>
        <CustomDataGrid
          tableId="alarmDisplay"
          rows={rowsData}
          columns={alarmColumns}
          rowCount={noOfRows}
          loading={isLoading}
          disableColumnMenu={true}
          getRowId={(row: IEvent) => row.objectKey}
          onSortModelChange={(params: GridSortModel) => setAlarmTableSort(getSortBy(params))}
          onRowClick={handleRowClicked}
          fillContainer
        />
      </div>
    </div>
  );
};

export default AlarmDisplay;